import i18n from "@/i18n";
import { Directive, DirectiveBinding, h, render, VNode } from "vue";

//#region Types
type TooltipDirection =
  | "top"
  | "top-left"
  | "top-right"
  | "bottom"
  | "bottom-left"
  | "bottom-right"
  | "left"
  | "left-top"
  | "left-bottom"
  | "right"
  | "right-top"
  | "right-bottom";

type PositioningValues = { direction: TooltipDirection; top: number; left: number };
//#endregion

//#region Globals
const { t: $t } = i18n.global;

const tooltips = new WeakMap<Element, { vNode: VNode; mods: Record<string, boolean> }>();

const possibleDirection: TooltipDirection[] = [
  "top",
  "top-left",
  "top-right",
  "bottom",
  "bottom-left",
  "bottom-right",
  "left",
  "left-top",
  "left-bottom",
  "right",
  "right-top",
  "right-bottom"
];
//#endregion

//#region Directive
const tooltipDirective: Directive = {
  mounted(el: HTMLElement, binding: DirectiveBinding) {
    setupTooltip(el, binding);
  },
  beforeUnmount(el: HTMLElement) {
    destroyTooltip(el);
  },
  updated(el: HTMLElement, binding: DirectiveBinding) {
    if (tooltips.has(el)) {
      destroyTooltip(el);
    }
    setupTooltip(el, binding);
  }
};
//#endregion

//#region Setup/Destroy
const setupTooltip = (el: HTMLElement, binding: DirectiveBinding) => {
  if (!binding.value) return;

  tooltips.set(el, { vNode: createTooltip(el, binding), mods: binding.modifiers });
  el.addEventListener("mouseleave", _mouseLeaveHandler, { passive: true });
  if (!binding.modifiers.mouse) {
    el.addEventListener("mouseover", _mouseOverHandler, { passive: true });
  } else {
    el.addEventListener("mousemove", _mouseMoveHandler, { passive: true });
  }
};

const destroyTooltip = (el: HTMLElement) => {
  render(null, document.body);
  el.removeEventListener("mouseover", _mouseOverHandler);
  el.removeEventListener("mouseleave", _mouseLeaveHandler);
  el.removeEventListener("mousemove", _mouseMoveHandler);
  tooltips.delete(el);
};

const createTooltip = (el: HTMLElement, binding: DirectiveBinding): VNode => {
  return h(
    "div",
    {
      class: ["m-tooltip", binding.modifiers.mouse ? "hovering" : null],
      style: {
        top: `0px`,
        left: `0px`
      }
    },
    [
      binding.modifiers.html
        ? h("div", { innerHTML: binding.value, class: "m-tooltip-content" })
        : h("div", { class: "m-tooltip-content", innerText: binding.value }),
      h("small", { class: ["m-tooltip-truncated-text"], innerText: $t("isTruncatedTooltip") }),
      !binding.modifiers.mouse ? h("div", { class: ["m-tooltip-arrow"] }) : null
    ]
  );
};
//#endregion

//#region EventHandler
const _mouseOverHandler = (event: MouseEvent) => {
  const el = event.currentTarget;
  if (!el || !(el instanceof HTMLElement)) return;

  const elRect = el.getBoundingClientRect();
  if (!checkIfMouseIsInsideElement(elRect, event)) return;

  const tooltipVNode = tooltips.get(el);
  if (!tooltipVNode || tooltipVNode.mods.mouse) return;

  if (!tooltipVNode.mods.ellipsis) render(tooltipVNode.vNode, document.body);
  else if (isElementOverflowing(el)) render(tooltipVNode.vNode, document.body);

  const tooltipHTMLElement: HTMLElement | null = document.querySelector(".m-tooltip");
  if (!tooltipHTMLElement) return;

  const tooltipContent: HTMLElement | null = tooltipHTMLElement.querySelector(".m-tooltip-content");
  if (isTextTruncated(tooltipContent)) tooltipHTMLElement.classList.add("truncated");

  const selectedMods = Object.keys(tooltipVNode.mods ?? {});
  const tooltipRect = tooltipHTMLElement?.getBoundingClientRect();
  const arrowSize = 6;
  const arrowElement = tooltipHTMLElement.querySelector(".m-tooltip-arrow");

  const viewportRect = document.body.getBoundingClientRect();

  const drawDirection: TooltipDirection = possibleDirection.find((x) => selectedMods.includes(x)) ?? "top";

  const postitioningValues = getPositioningValues(drawDirection, elRect, tooltipRect, arrowSize, viewportRect);

  const previousDrawDirectionClass = arrowElement?.classList.item(1);
  if (previousDrawDirectionClass) arrowElement?.classList.remove(previousDrawDirectionClass);
  arrowElement?.classList.add(postitioningValues.direction);

  tooltipHTMLElement.style.top = `${postitioningValues.top}px`;
  tooltipHTMLElement.style.left = `${postitioningValues.left}px`;
};
const _mouseLeaveHandler = () => {
  render(null, document.body);
};

const _mouseMoveHandler = (event: MouseEvent) => {
  const el = <HTMLElement>event.currentTarget;
  const tooltipVNode = tooltips.get(el);
  if (!tooltipVNode) return;

  if (!tooltipVNode.mods.ellipsis) render(tooltipVNode?.vNode, document.body);
  else if (isElementOverflowing(el)) render(tooltipVNode?.vNode, document.body);

  const tooltipHTMLElement: HTMLElement | null = document.querySelector(".m-tooltip");
  if (!tooltipHTMLElement) return;

  const tooltipContent: HTMLElement | null = tooltipHTMLElement.querySelector(".m-tooltip-content");
  if (isTextTruncated(tooltipContent)) tooltipHTMLElement.classList.add("truncated");

  const offSet = 12;
  const viewportRect = document.querySelector(".content-inner-container")?.getBoundingClientRect() ?? document.body.getBoundingClientRect();
  const tooltipRect = tooltipHTMLElement.getBoundingClientRect();

  const left = event.clientX + offSet;
  const top = event.clientY + offSet;

  if (isInsideViewport(top, left, tooltipRect, viewportRect)) {
    tooltipHTMLElement.style.top = `${top}px`;
    tooltipHTMLElement.style.left = `${left}px`;
  } else {
    tooltipHTMLElement.style.top = `${event.clientY - tooltipRect.height - offSet}px`;
    tooltipHTMLElement.style.left = `${event.clientX - tooltipRect.width - offSet}px`;
  }
};

//#endregion

//#region Util

function getPositioningValues(
  drawDirection: TooltipDirection,
  elRect: DOMRect,
  tooltipRect: DOMRect,
  arrowSize: number,
  viewportRect: DOMRect,
  executingDepth: number = 0
): PositioningValues {
  const positioningValues: PositioningValues = { direction: drawDirection, top: 0, left: 0 };

  let xFactor = [0, 0, 0];
  let yFactor = [0, 0, 0];

  switch (drawDirection) {
    case "left":
      xFactor = [0, -1, -1];
      yFactor = [0.5, -0.5, 0];
      break;
    case "left-top":
      xFactor = [0, -1, -1];
      yFactor = [0.5, -1, 1];
      break;
    case "left-bottom":
      xFactor = [0, -1, -1];
      yFactor = [0.5, 0, -1];
      break;
    case "right":
      xFactor = [1, 0, 1];
      yFactor = [0.5, -0.5, 0];
      break;
    case "right-top":
      xFactor = [1, 0, 1];
      yFactor = [0.5, -1, 1];
      break;
    case "right-bottom":
      xFactor = [1, 0, 1];
      yFactor = [0.5, 0, -1];
      break;
    case "bottom":
      xFactor = [0.5, -0.5, 0];
      yFactor = [1, 0, 1];
      break;
    case "bottom-left":
      xFactor = [0.5, -1, 3];
      yFactor = [1, 0, 1];
      break;
    case "bottom-right":
      xFactor = [0.5, 0, -3];
      yFactor = [1, 0, 1];
      break;
    case "top-left":
      xFactor = [0.5, -1, 2];
      yFactor = [0, -1, -1];
      break;
    case "top-right":
      xFactor = [0.5, 0, -3];
      yFactor = [0, -1, -1];
      break;
    case "top":
    default:
      xFactor = [0.5, -0.5, 0];
      yFactor = [0, -1, -1];
      break;
  }
  positioningValues.left = elRect.x + elRect.width * xFactor[0] + tooltipRect.width * xFactor[1] + arrowSize * xFactor[2];
  positioningValues.top = elRect.y + elRect.height * yFactor[0] + tooltipRect.height * yFactor[1] + arrowSize * yFactor[2];
  if (executingDepth < 12) {
    while (!isInsideViewport(positioningValues.top, positioningValues.left, tooltipRect, viewportRect)) {
      return getPositioningValues(rotateDirectionClockwise(positioningValues.direction), elRect, tooltipRect, arrowSize, viewportRect, executingDepth + 1);
    }
  }

  return positioningValues;
}

function isInsideViewport(top: number, left: number, tooltipRect: DOMRect, viewportRect: DOMRect): boolean {
  let result = false;

  result = result || top < viewportRect.y; ///top-check
  result = result || top + tooltipRect.height > viewportRect.height; ///bottom-check
  result = result || left < viewportRect.x; ///left-check
  result = result || left + tooltipRect.width > viewportRect.width; ///right-check

  return !result;
}

function rotateDirectionClockwise(direction: TooltipDirection): TooltipDirection {
  const directions: TooltipDirection[] = [
    "top",
    "top-right",
    "right-top",
    "right",
    "right-bottom",
    "bottom-right",
    "bottom",
    "bottom-left",
    "left-bottom",
    "left",
    "left-top",
    "top-left"
  ];
  const currentIndex = directions.indexOf(direction);
  const nextIndex = (currentIndex + 1) % directions.length;
  return directions[nextIndex];
}

const isElementOverflowing = (el: HTMLElement): boolean => {
  let isOverflowing = isTextTruncated(el);
  if (!isOverflowing) {
    for (const child of el.children) {
      if (child instanceof HTMLElement) {
        if (child.children.length) {
          isOverflowing = isOverflowing || isElementOverflowing(child);
        }
        isOverflowing = isOverflowing || isTextTruncated(child);
      }
      if (isOverflowing) break;
    }
  }

  return isOverflowing;
};

function isTextTruncated(node: HTMLElement | null) {
  if (!node) return false;
  return node.scrollHeight > node.clientHeight || node.scrollWidth > node.clientWidth;
}

function checkIfMouseIsInsideElement(elRect: DOMRect, event: MouseEvent) {
  return elRect.top <= event.clientY && event.clientY <= elRect.bottom && elRect.left <= event.clientX && event.clientX <= elRect.right;
}

//Maybe it will become useful in the Future, but to reduce complexity and checks if remains commented out
//
// function findElementsOnTop(targetElement: HTMLElement) {
//   const overlappingElements = new Set();
//   const rect = targetElement.getBoundingClientRect();

//   // Sample multiple points across the target element
//   const pointsToCheck = [
//     { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }, // center
//     { x: rect.left + 10, y: rect.top + 10 }, // top-left
//     { x: rect.right - 10, y: rect.top + 10 }, // top-right
//     { x: rect.left + 10, y: rect.bottom - 10 }, // bottom-left
//     { x: rect.right - 10, y: rect.bottom - 10 } // bottom-right
//   ];

//   pointsToCheck.forEach(({ x, y }) => {
//     const topElement = document.elementFromPoint(x, y);

//     // Check if the top element is not the target itself
//     if (topElement && !topElement.isSameNode(targetElement) && !targetElement.contains(topElement)) {
//       overlappingElements.add(topElement);
//     }
//   });

//   return Array.from(overlappingElements);
// }
//#endregion

export default tooltipDirective;
